/**
 * Клиент AI-разбора.
 *
 * Ключ OpenAI живёт только на сервере (Vercel serverless в /api/ai-report).
 * Если функция недоступна или ключ не настроен — показываем разбор,
 * собранный из расчётной модели, чтобы продукт не ломался.
 */

import { buildFallbackReport } from './fallbackReport';
import { getChannelWhitelist } from '../../../data/diagnostic/channels';
import { sanitizeAiReport } from '../../../utils/inputSecurity';

const ENDPOINT = '/api/ai-report';
const TIMEOUT_MS = 45000;
const MAX_RESPONSE_BYTES = 50000;

/**
 * В запрос уходят только уже посчитанные числа и справочные данные,
 * чтобы модель ничего не выдумывала от себя.
 */
export const buildRequestPayload = (result) => {
  const scenario = result.recommended || result.scenarios[0] || null;

  return {
    niche: {
      id: String(result.niche.id || '').slice(0, 40),
      label: String(result.niche.label || '').slice(0, 80),
    },
    current: {
      revenue: Math.round(result.state.revenue),
      averageCheck: Math.round(result.state.averageCheck),
      clients: Math.round(result.state.clients),
      leads: Math.round(result.state.leads),
      conversionPercent: Number(result.state.conversionPercent.toFixed(1)),
      hasConversion: Boolean(result.state.hasConversion),
    },
    target: {
      revenue: Math.round(result.targetRevenue),
      months: result.input.targetMonths || null,
      status: result.targetEvaluation.status,
    },
    ceiling: {
      revenue: Math.round(result.ceiling.revenue),
      growthPercent: Number(result.ceiling.growthPercent.toFixed(1)),
    },
    scenarios: result.scenarios.slice(0, 3).map((item) => ({
      id: String(item.id || '').slice(0, 32),
      name: String(item.name || '').slice(0, 40),
      achievedRevenue: Math.round(item.achievedRevenue),
      targetCoverage: Number(item.targetCoverage.toFixed(1)),
      closesTarget: Boolean(item.closesTarget),
      timing: String(item.timing.label || '').slice(0, 40),
      levers: item.levers
        .filter((lever) => lever.growth > 0)
        .slice(0, 4)
        .map((lever) => ({
          name: String(lever.plainLabel || '').slice(0, 80),
          growthPercent: Number(lever.growthPercent.toFixed(1)),
          atCap: Boolean(lever.atCap),
        })),
    })),
    recommendedScenarioId: scenario ? String(scenario.id).slice(0, 32) : null,
    allowedChannels: getChannelWhitelist(result.niche.id).slice(0, 12),
  };
};

export const requestAiReport = async (result) => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const response = await fetch(ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(buildRequestPayload(result)),
      signal: controller.signal,
      credentials: 'same-origin',
    });

    if (!response.ok) {
      return { report: buildFallbackReport(result), source: 'fallback', status: response.status };
    }

    const rawText = await response.text();
    if (!rawText || rawText.length > MAX_RESPONSE_BYTES) {
      return { report: buildFallbackReport(result), source: 'fallback', status: 'oversized' };
    }

    let data;
    try {
      data = JSON.parse(rawText);
    } catch {
      return { report: buildFallbackReport(result), source: 'fallback', status: 'invalid' };
    }

    const report = sanitizeAiReport(data);
    if (!report) {
      return { report: buildFallbackReport(result), source: 'fallback', status: 'invalid' };
    }

    return { report: { ...report, fallback: false }, source: 'ai' };
  } catch (error) {
    const reason = error.name === 'AbortError' ? 'timeout' : 'network';
    return { report: buildFallbackReport(result), source: 'fallback', status: reason };
  } finally {
    clearTimeout(timeout);
  }
};
